import {isUndefined} from 'lodash';
import React, {useMemo, useRef} from 'react';
import styled from 'styled-components';

import {HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {useSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsDonutMetric,
  AnalyticsDonutMetricValue,
  AnalyticsGraphMetric,
  AnalyticsGraphMetricsEnum,
  AnalyticsMetricError,
  AnalyticsNumericDataPointType,
  AnalyticsScalarMetric,
  AnalyticsScalarMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {makeGetAnalyticsMetric} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {getVariationConfig, renderFormattedMetricData} from '../../../helpers/analyticsFormatHelpers';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {renderPercentageValue} from '../../../helpers/metricRenderers/percentMetricRenderer';
import {AnalyticsComputedGraphMetric} from '../../../transformers/analyticsMetricsTransformers';
import {AnalyticsComputedGraphMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';
import {PieChart} from '../../charts/components/pieChart/pieChart';
import {StyledElementWrapper} from '../../reportViewer/reportCommons';
import {ReportElementProps, ReportElementWrapper, ReportElementWrapperProps} from '../reportElement';
import {ChartMetricValue, ChartProps} from './chartsModel';
import {defaultErrorStateRenderer} from './common/metricErrorState';
import {defaultEmptyStateRenderer, isEmptyMetric} from './emptyState';
import {useGraphMetricData} from './useGraphMetricData';

/*
 * Constants.
 */

const DEFAULT_HEIGHT = 275;

const mockData: ReadonlyArray<AnalyticsDonutMetricValue> = [
  {
    value: 0,
    previousValue: 0,
    label: '',
    type: AnalyticsDataPointTypesEnum.PERCENTAGE,
  },
  {
    // We want palette[grey].shade20 for the whole donut so it has to match the second entry of the color palette when computing colors.
    // See https://github.com/frontapp/components/blob/3d12abf988ee5496f0834222dc9345a4204dfba4/packages/charts/src/helpers/colors.ts#L8 for implem.
    value: 1,
    previousValue: 1,
    label: '',
    type: AnalyticsDataPointTypesEnum.PERCENTAGE,
  },
];

/*
 * Props.
 */

export interface ReportDonutChartProps<T>
  extends ReportElementWrapperProps,
    ChartProps,
    ReportElementProps<AnalyticsGraphMetricsEnum | AnalyticsComputedGraphMetricsEnum> {
  accessor: (d: T) => number;
  /** If specified, will be displayed in the center of the donut */
  innerMetric?: AnalyticsScalarMetricsEnum;
  innerMetricType?: AnalyticsNumericDataPointType;
  /** Manage click effects. */
  onArcClick?: (index: number, item?: AnalyticsDonutMetricValue) => void;
  /** Manage hover effects. */
  hoveredItemId?: string;
  onArcHover?: HandlerOf<number | undefined>;
  /** Custom label renderer - default will display % value */
  labelRenderer?: RendererOf<T>;
  /** Needed to bind hover events to a ReportList */
  reportListBindId?: string;
  renderErrorState?: RendererOf<{metricError: AnalyticsMetricError; width?: number; heigth?: number}>;
  color?: PaletteColorsEnum;
}

/*
 * Styles.
 */

const StyledInnerMetricLabel = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  line-height: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
`;

const StyledInnerMetricValue = styled.div`
  margin: 8px 0;
  color: ${(p) => p.theme.greys.shade80};
  font-size: 24px;
  font-weight: ${fontWeights.semibold};
`;

const StyledInnerMetricVariation = styled.div<{$color: string}>`
  width: 100%;
  ${flexCenter}
  justify-content: center;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: ${fontSizes.large};

  color: ${(p) => p.$color};
`;

const DonutWrapper = styled(StyledElementWrapper)`
  height: 100%;
`;

/*
 * Component.
 */

const maybeDisplayInnerData = (
  theme: UserTheme,
  innerMetricData?: AnalyticsScalarMetric,
  innerMetricError?: AnalyticsMetricError,
  innerMetricType?: AnalyticsNumericDataPointType,
  isLoading?: boolean,
) => {
  // If there is an error with the inner metric we just display nothing - since trying to display
  // an error state specific to the inner metric when just it errors but the donut as a whole does not
  // it only likely to confuse the user.
  if (!innerMetricData || !innerMetricType || isLoading || innerMetricError) {
    return null;
  }

  const {label} = getMetricLabels(innerMetricData.id);
  const {variationPct, icon, color} = getVariationConfig(
    innerMetricData.value,
    innerMetricData.previousValue,
    theme,
  );
  const renderedMetricValue = renderFormattedMetricData(innerMetricData, innerMetricType);
  return (
    <>
      <StyledInnerMetricLabel>{label}</StyledInnerMetricLabel>
      <StyledInnerMetricValue>{renderedMetricValue}</StyledInnerMetricValue>
      {icon && color && (
        <StyledInnerMetricVariation $color={color}>
          <Icon name={icon} color={color} />
          {variationPct && renderPercentageValue({value: variationPct})}
        </StyledInnerMetricVariation>
      )}
    </>
  );
};

export const ReportDonutChart = <T extends ChartMetricValue>({
  title,
  reportSectionId,
  isLoading,
  renderEmptyState = defaultEmptyStateRenderer,
  renderErrorState = defaultErrorStateRenderer,
  metric,
  innerMetric,
  innerMetricType,
  width,
  height,
  accessor,
  reportListBindId,
  hoveredItemId,
  onArcClick,
  onArcHover,
  labelRenderer,
  color = PaletteColorsEnum.BLUE,
}: ReportDonutChartProps<T>) => {
  const theme = useUserTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSizeInfo = useSize(containerRef);

  const [graphMetricData, metricError] = useGraphMetricData<AnalyticsDonutMetricValue, AnalyticsDonutMetric>(
    metric,
    (metricData?: AnalyticsGraphMetric | AnalyticsComputedGraphMetric) =>
      isLoading && (!metricData || metricData.values?.length === 0),
    mockData,
  );
  const [innerMetricData, innerMetricError] = useAppSelector(makeGetAnalyticsMetric(innerMetric));

  const handleArcClick = (index: number) => {
    if (onArcClick) {
      onArcClick(index, graphMetricData?.values[index]);
    }
  };

  const hoveredArcIndex = useMemo(() => {
    if (!hoveredItemId) {
      return undefined;
    }

    const [listBindId, listItemId] = hoveredItemId.split('-'); // hoveredItemId would be something like 'reply_times-3'.

    return reportListBindId === listBindId ? Number(listItemId) : undefined;
  }, [hoveredItemId, reportListBindId]);

  const isEmpty = !isLoading && isEmptyMetric(graphMetricData);

  const renderChart = () => {
    if (metricError) {
      return renderErrorState({metricError, width: width ?? containerSizeInfo?.width});
    }

    if (isEmpty) {
      return renderEmptyState({width: width ?? containerSizeInfo?.width});
    }

    if (containerSizeInfo && graphMetricData) {
      return (
        <PieChart<T>
          width={width ?? containerSizeInfo.width}
          height={height ?? DEFAULT_HEIGHT}
          isLoading={isLoading}
          color={color}
          accessor={accessor}
          data={(graphMetricData?.values as Array<T>) ?? mockData}
          renderLabel={makeRenderLabel(labelRenderer)}
          hoveredArcIndex={hoveredArcIndex}
          onArcClick={handleArcClick}
          onArcHover={(arcIndex?: number) => onArcHover && onArcHover(arcIndex)}
        >
          {maybeDisplayInnerData(theme, innerMetricData, innerMetricError, innerMetricType, isLoading)}
        </PieChart>
      );
    }

    return null;
  };

  return (
    // If the report is empty, we want to use the default behavior of displaying it as full height.
    <ReportElementWrapper
      title={title}
      reportSectionId={reportSectionId}
      hasFullHeight={isEmpty || !isUndefined(metricError)}
    >
      <DonutWrapper ref={containerRef}>{renderChart()}</DonutWrapper>
    </ReportElementWrapper>
  );
};

/*
 * Helpers.
 */

function makeRenderLabel<T>(labelRenderer?: RendererOf<T>) {
  return function finalLabelRenderer(d: T, x: number, y: number, i: number, percent: number) {
    const defaultLabel = `${percent}%`;
    return (
      <UserThemeConsumer>
        {(theme) => (
          <text
            x={x}
            y={y}
            dy=".33em"
            fill={i % 5 < 3 ? theme.greys.shade90 : theme.greys.white}
            fontSize={10}
            fontWeight="bold"
            textAnchor="middle"
            pointerEvents="none"
          >
            {labelRenderer ? labelRenderer(d) : defaultLabel}
          </text>
        )}
      </UserThemeConsumer>
    );
  };
}
